export interface RouteMetadata {
  title: string;
  announcement: string;
}

interface RouteMetadataRule {
  matches: (pathname: string) => boolean;
  title: string;
}

const exact = (expected: string) => (pathname: string) => pathname === expected;
const pattern = (expected: RegExp) => (pathname: string) => expected.test(pathname);

/**
 * Route titles are deliberately independent from fetched entity names. This
 * keeps the document title truthful during loading, denied, and failed states.
 */
const ROUTE_METADATA: readonly RouteMetadataRule[] = [
  { matches: exact('/admin'), title: 'Platform administration' },
  { matches: exact('/'), title: 'Dashboard' },
  { matches: exact('/settle-up'), title: 'Settle Up' },
  { matches: pattern(/^\/transactions\/[^/]+\/edit\/?$/), title: 'Edit transaction' },
  { matches: pattern(/^\/record(?:\/[^/]+)?\/?$/), title: 'Record' },
  { matches: pattern(/^\/transactions\/new(?:\/[^/]+)?\/?$/), title: 'Record' },
  { matches: exact('/transactions'), title: 'Transactions' },
  { matches: exact('/approvals'), title: 'Approvals' },
  { matches: pattern(/^\/accounts\/[^/]+\/?$/), title: 'Account statement' },
  { matches: exact('/accounts'), title: 'Accounts' },
  { matches: pattern(/^\/customers\/[^/]+\/?$/), title: 'Customer details' },
  { matches: exact('/customers'), title: 'Customers' },
  { matches: exact('/invoices/payment'), title: 'Allocate customer payment' },
  { matches: exact('/invoices/new'), title: 'Create invoice' },
  { matches: pattern(/^\/invoices\/[^/]+\/edit\/?$/), title: 'Edit invoice' },
  { matches: pattern(/^\/invoices\/[^/]+\/?$/), title: 'Invoice details' },
  { matches: exact('/invoices'), title: 'Invoices' },
  { matches: pattern(/^\/budgets\/[^/]+\/?$/), title: 'Budget details' },
  { matches: exact('/budgets'), title: 'Budgets' },
  { matches: exact('/reports'), title: 'Reports' },
  { matches: exact('/settings/company'), title: 'Company settings' },
  { matches: exact('/settings/members'), title: 'Members & invitations' },
  { matches: exact('/settings/roles'), title: 'Roles & permissions' },
  { matches: exact('/settings/categories'), title: 'Categories' },
  { matches: exact('/settings/currencies'), title: 'Currencies & rates' },
  { matches: exact('/settings/approval-rules'), title: 'Approval rules' },
  { matches: exact('/settings/audit-log'), title: 'Audit log' },
  { matches: exact('/settings/integrations'), title: 'Integrations' },
  { matches: pattern(/^\/settings(?:\/)?$/), title: 'Settings' },
  { matches: exact('/shared/new'), title: 'Create shared group' },
  { matches: pattern(/^\/shared\/groups\/[^/]+(?:\/.*)?$/), title: 'Shared group' },
];

function normalizedPathname(pathname: string): string {
  if (!pathname) return '/';
  const withLeadingSlash = pathname.startsWith('/') ? pathname : `/${pathname}`;
  return withLeadingSlash.length > 1 ? withLeadingSlash.replace(/\/+$/, '') : withLeadingSlash;
}

export function getRouteMetadata(pathname: string): RouteMetadata {
  const normalized = normalizedPathname(pathname);
  const title = ROUTE_METADATA.find((rule) => rule.matches(normalized))?.title ?? 'Finance workspace';
  return {
    title,
    announcement: `${title} page loaded`,
  };
}

export function documentTitleForPath(pathname: string): string {
  return `${getRouteMetadata(pathname).title} — Ledger Mono`;
}
